// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { NotificationData } from '../types/notification';
import { NotificationItem } from './NotificationItem';
import { NotificationDateGroup } from './NotificationDateGroup';
import { NotificationEmptyState } from './NotificationEmptyState';
import { AppIcon } from './AppIcon';
import { IconButton } from './IconButton';
import './NotificationPanel.css';

export interface NotificationPanelGroup {
  label: string;
  items: Array<{
    notification: NotificationData;
    timeLabel: string;
  }>;
}

export interface NotificationPanelProps {
  groups: NotificationPanelGroup[];
  unreadCount: number;
  loading?: boolean;
  hasMore?: boolean;
  onItemClick?: (id: string) => void;
  onItemDelete?: (id: string) => void;
  onMarkAllRead?: () => void;
  onViewAll?: () => void;
  onLoadMore?: () => void;
  /** When provided, renders a close (×) button in the header. Used when the
   *  panel is hosted inside a modal (e.g. the client UI mobile bell), so the
   *  user has the same dismiss affordance as a Modal.Header. */
  onClose?: () => void;
  className?: string;
}

export function NotificationPanel({
  groups,
  unreadCount,
  loading = false,
  hasMore = false,
  onItemClick,
  onItemDelete,
  onMarkAllRead,
  onViewAll,
  onLoadMore,
  onClose,
  className = '',
}: NotificationPanelProps) {
  const classes = ['fin-notif-panel', className].filter(Boolean).join(' ');

  const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);
  const isEmpty = totalItems === 0 && !loading;

  return (
    <div className={classes}>
      {/* Header */}
      <div className="fin-notif-panel__header">
        <div className="fin-notif-panel__title-row">
          <span className="fin-notif-panel__title">Notifications</span>
          {unreadCount > 0 && (
            <span className="fin-notif-panel__unread-count">
              ({unreadCount} unread)
            </span>
          )}
        </div>
        {unreadCount > 0 && onMarkAllRead && (
          <button
            className="fin-notif-panel__mark-all"
            onClick={onMarkAllRead}
            type="button"
          >
            <AppIcon name="check" size="tiny" />
            <span>Mark all as read</span>
          </button>
        )}
        {onClose && (
          <span className="fin-notif-panel__close">
            <IconButton
              variant="ghost"
              size="xs"
              icon={<AppIcon name="close" />}
              onClick={onClose}
              aria-label="Close"
            />
          </span>
        )}
      </div>

      <div className="fin-notif-panel__divider" />

      {/* List */}
      <div className="fin-notif-panel__list">
        {loading && totalItems === 0 ? (
          <div className="fin-notif-panel__loader">
            <span className="fin-notif-panel__spinner" />
          </div>
        ) : isEmpty ? (
          <NotificationEmptyState />
        ) : (
          <>
            {groups.map((group) => (
              <div key={group.label}>
                <NotificationDateGroup label={group.label} />
                {group.items.map(({ notification, timeLabel }) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    timeLabel={timeLabel}
                    onClick={onItemClick}
                    onDelete={onItemDelete}
                  />
                ))}
              </div>
            ))}

            {hasMore && (
              <div className="fin-notif-panel__load-more">
                <button
                  className="fin-notif-panel__load-more-btn"
                  onClick={onLoadMore}
                  disabled={loading}
                  type="button"
                >
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      {onViewAll && (
        <>
          <div className="fin-notif-panel__divider" />
          <div className="fin-notif-panel__footer">
            <button
              className="fin-notif-panel__view-all"
              onClick={onViewAll}
              type="button"
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );
}
